// 2026-05-26 - FormuLearning - Step 2 물리화학적 특성 비교 섹션
// 2026-11-03 수정 - 이슈 #114: viewer 는 «Run DB Search & AI Prediction»·출처 선택 비활성(선택 표시는 그대로 보인다).
// 파일 위치: frontend/src/features/step2/PhyschemSection.tsx
// 2026-07-29 (이슈 #51): AI Prediction 실예측(OPERA) 도입 — 신뢰도·적용범위 병기.
// 2026-08-24 (이슈 #62): M84-42 Lipinski 확장 이동·pH별 LogD 표 제거, M84-44 `Selected` 열 삭제.
// 2026-09-23 (이슈 #83): 예측 중 **Break** 버튼(`onBreak`) — 중단하면 저장되지 않고 이전 결과로 돌아간다.
// 2026-10-29 (이슈 #111): M1016-02 «pKb (염기성)» → «pKa (염기성)» 라벨 정정.
// 2026-11-03 (이슈 #104): 고객 시트 노랑 항목만 표시 — «Show all» 확장(Lipinski·pKa 산성/염기성·VP·Henry·KOA) 삭제,
//   pKa 행 AI 칸에 산성·염기성 병기(설계 v1.1 D-261103-03). 산출·저장 키는 그대로다(보고서·AI 해설 참조).

import type { AiPrediction } from '../../api/step2'
import { QualityBadge, QualityLegend } from './PredictionQuality'
import { useCanWrite, READ_ONLY_TITLE } from '../../hooks/useCanWrite'

interface Props {
  dbSearch: Record<string, number | string | null>
  aiPrediction: AiPrediction
  selectedSource: Record<string, string>
  onSelectSource: (prop: string, source: 'db' | 'ai') => void
  onPredict: () => void
  predicting: boolean
  onBreak?: () => void
  breaking?: boolean
  smiles: string | null
}

/** 기본 표시 — 제형설계에서 상시 참조하는 7항목 (REQ-FN-2-01 PPT 기준)
 *  2026-08-24 (M84-42): Lipinski Rule 을 확장 항목으로 이동 — 안건5. */
const PROP_LABELS: Record<string, string> = {
  pka: 'pKa',
  logp: 'LogP',
  boiling_point: 'Boiling Point (°C)',
  caco2: 'Caco-2 (×10⁻⁶ cm/s)',
  molecular_weight: 'MW (g/mol)',
  melting_point: 'Melting Point (°C)',
  water_solubility: 'Water Sol. (mg/mL)',
}


/** 지수 표기가 필요한 소수는 읽기 쉽게 다듬는다 (1.9e-11 등). */
function display(v: number | string | null): string {
  if (v == null) return '—'
  if (typeof v === 'string') return v
  if (v !== 0 && Math.abs(v) < 1e-3) return v.toExponential(2)
  return String(Number(v.toFixed(4)))
}

/** 표시 가능한 물성 값만 꺼낸다. 밑줄 접두 메타 키(_quality·_source·_errors)는 걸러진다. */
function aiValue(ai: AiPrediction, key: string): number | string | null {
  const v = ai[key]
  return typeof v === 'number' || typeof v === 'string' ? v : null
}

/** pKa AI 칸 — 고객 시트 노랑은 산성·염기성 2값이다(#104). 행은 DB Search 단일 pKa 와 짝이라 1행으로 두고 병기한다.
 *  산성·염기성 키가 없는 옛 저장분은 대표값(`pka`)을 그대로 보인다. */
function pkaText(ai: AiPrediction): string {
  const a = aiValue(ai, 'pka_acidic')
  const b = aiValue(ai, 'pka_basic')
  if (a == null && b == null) return display(aiValue(ai, 'pka'))
  return [a != null ? `a ${display(a)}` : null, b != null ? `b ${display(b)}` : null].filter(Boolean).join(' · ')
}

export default function PhyschemSection({ dbSearch, aiPrediction, selectedSource, onSelectSource, onPredict, predicting, onBreak, breaking, smiles }: Props) {
  const canWrite = useCanWrite() // viewer(#114) — 조회·예측 실행과 DB/AI 출처 선택(저장) 비활성
  // 표시 항목은 고객 시트 노랑 기준 7행 고정(#104). 정의된 라벨 순서를 유지한다.
  const props = Object.keys(PROP_LABELS)

  const quality = aiPrediction._quality ?? {}
  const source = aiPrediction._source
  const hasQuality = Object.keys(quality).length > 0

  const hasData = Object.keys(dbSearch).length > 0 || Object.keys(aiPrediction).length > 0

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">Physicochemical Properties</h4>
        <div className="flex items-center">
        {/* 2026-08-04 - 버튼 상시 노출(이슈 #55). 종전에는 smiles 가 없으면 버튼 자체를 숨겨,
            신규 프로젝트에서 버튼이 사라진 것으로 보였다. 이제 비활성 상태로 남기고 사유를
            툴팁으로 알린다. SMILES 확보 즉시 활성화되는 것은 moleculeStore 구독이 보장한다. */}
        <button
          onClick={onPredict}
          disabled={predicting || !smiles || !canWrite}
          title={!canWrite ? READ_ONLY_TITLE : smiles ? undefined : 'Step 1에서 성분을 검색해 분자 정보(SMILES)를 먼저 확보하세요.'}
          className="rounded px-3 py-1 text-[10px] font-medium text-accent-teal transition-colors hover:bg-bg-input disabled:cursor-not-allowed disabled:opacity-50"
          style={{ border: '1px solid var(--border-teal)' }}
        >
          {predicting ? 'Running…' : 'Run DB Search & AI Prediction'}
        </button>
        {predicting && onBreak && (
          <button
            onClick={onBreak}
            disabled={breaking}
            className="ml-2 rounded px-2 py-1 text-[10px] font-semibold text-red-400 transition-colors hover:bg-red-500/10 disabled:opacity-50"
            data-testid="step2-break"
          >
            {breaking ? 'Breaking...' : 'Break'}
          </button>
        )}
        </div>
      </div>

      <div
        className="mb-2 rounded border px-3 py-2 text-[10px] leading-relaxed"
        style={{
          borderColor: 'color-mix(in srgb, var(--color-warn) 35%, transparent)',
          background: 'color-mix(in srgb, var(--color-warn) 10%, transparent)',
          color: 'var(--color-warn)',
        }}
      >
        <span className="font-semibold">🚧 개발 노트</span>
        <ul className="mt-1 list-disc pl-4">
          <li>제약용 Property 필요 항목 선정 및 전달 필요 (6/25~)</li>
          <li>AI Prediction = OPERA v2.9 QSAR 예측값 (7/29 적용). 실측값이 아니므로 신뢰도 배지를 함께 확인</li>
        </ul>
      </div>

      {hasData ? (
        <div className="overflow-x-auto rounded border" style={{ borderColor: 'var(--border-subtle)' }}>
          <table className="w-full text-[11px]">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-bg-panel)' }}>
                <th className="px-3 py-1.5 text-left font-medium text-text-secondary">Property</th>
                <th className="px-3 py-1.5 text-center font-medium text-text-secondary">DB Search</th>
                <th className="px-3 py-1.5 text-center font-medium text-text-secondary">AI Prediction</th>
              </tr>
            </thead>
            <tbody>
              {props.map((key) => {
                // `Selected` 열은 M84-44 로 삭제됐다. 선택 상태는 값 버튼 하이라이트가 표시하며,
                // 저장(`selected_source`)·게이팅·AI Summary 프롬프트는 종전대로 이 값을 쓴다.
                const selected = selectedSource[key] || 'db'
                return (
                  <tr key={key} className="border-t" style={{ borderColor: 'var(--border-subtle)' }}>
                    <td className="px-3 py-1.5 font-medium text-text-body">{PROP_LABELS[key]}</td>
                    <td className="px-3 py-1.5 text-center">
                      <button
                        onClick={() => onSelectSource(key, 'db')}
                        disabled={!canWrite}
                        title={canWrite ? undefined : READ_ONLY_TITLE}
                        className="rounded px-2 py-0.5 text-text-body transition-colors disabled:cursor-default"
                        style={selected === 'db' ? { background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' } : {}}
                      >
                        {display(dbSearch[key] ?? null)}
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-center whitespace-nowrap">
                      <button
                        onClick={() => onSelectSource(key, 'ai')}
                        disabled={!canWrite}
                        title={canWrite ? undefined : READ_ONLY_TITLE}
                        className="rounded px-2 py-0.5 text-text-body transition-colors disabled:cursor-default"
                        style={selected === 'ai' ? { background: 'var(--bg-chip-teal)', color: 'var(--color-accent-teal)' } : {}}
                      >
                        {key === 'pka' ? pkaText(aiPrediction) : display(aiValue(aiPrediction, key))}
                      </button>
                      {quality[key] && <QualityBadge quality={quality[key]} />}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      ) : null}


      {hasData && hasQuality && <QualityLegend source={source} />}

      {!hasData && (
        <p className="text-xs text-text-secondary">Search a compound in Step 1, then run DB Search &amp; AI Prediction here.</p>
      )}
    </div>
  )
}
